import { useNavigate } from "react-router-dom";
import { Form } from "antd";
import { useState } from "react";
import { useSelector } from "react-redux";

import * as parkingServices from "../../../services/parkingServices";
import { CustomNotification } from "../../../util/CustomNotification";
import useValue from "../../../hooks/useValue";
import { ErrorMessageConstants } from "../../../constants/MessageConstants/ErrorMessageConstants";

import type { keyFOBDetails } from "../../../shared/interface/parking.interface";
import type { RootState } from "../../../appRedux/store";

interface useCreateParkingProps {
  handleRefreshList: () => void | undefined;
  handleOnClose: () => void | undefined;
}

const useCreateParking = (props: useCreateParkingProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();

  /* STATE */
  const navigate = useNavigate();
  const [keyFOBDetails, setKeyFOBDetails] = useState<keyFOBDetails[] | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [selectedUserName, setSelectedUserName] = useState<number | null>(null); // Store selected user ID

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);

  /* HANDLER */

  const handleBack = () => {
    navigate(-1);
  };

  const resetField = () => {
    /* reset mobile_user_id*/
    form.setFieldValue("mobile_user_id", null);
    setKeyFOBDetails(null);
    setIsSubmitDisabled(true);
    setSelectedUserName(null); // Reset the selected user name
  };

  const handleChangeKeyFob = () => {
    resetField();
  };

  const handleVerifyKeyFOB = async () => {
    try {
      const keyFOB = form.getFieldValue("keyFOB");

      if (!keyFOB) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.KEY_FOB_TOKEN_REQUIRED);
        return;
      }

      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      const response = await parkingServices.validateKeyFob(keyFOB, masterFilters?.customerId, masterFilters?.locationId);

      const formattedResponse = response?.data?.map((item) => {
        return {
          ...item,
          fullName: `${item?.mobileUser?.first_name} ${item?.mobileUser?.last_name}`
        };
      });

      // Pick the first record in the array
      const selectedRecord = formattedResponse?.[0] || null;

      // Set the selected user as the first user if available
      if (selectedRecord) {
        setIsSubmitDisabled(false);
        setKeyFOBDetails(formattedResponse);
        setSelectedUserName(selectedRecord?.mobileUser?.id);

        // Automatically select the last user in the list for user name field
        form.setFieldsValue({ mobile_user_id: selectedRecord?.mobileUser?.id });
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleUserNameChange = (value: number) => {
    setSelectedUserName(value); // Update the selected user ID
  };

  const findRecordByKeyFobTokenAndUserId = (records: keyFOBDetails[] | null, keyFobToken: number, userId: number) => {
    return records?.find((record) => record?.keyFob?.key_fob_token === keyFobToken && record?.mobileUser?.id === userId);
  };

  const handleSubmit = async (data: { mobile_user_id: number; keyFOB: string; request_type_id: number }) => {
    try {
      /* assign requestType ID */

      handleEnableLoading();

      const keyFobToken = Number(data.keyFOB); // Convert keyFOB from string to number

      const getRecord = findRecordByKeyFobTokenAndUserId(keyFOBDetails, keyFobToken, data.mobile_user_id);

      if (!getRecord?.keyFob?.id) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.KEY_FOB_ID_REQUIRED);
        return;
      }

      // Creating data object to send to API
      const dataToSendOb = {
        request_type_id: data.request_type_id,
        key_fob_id: getRecord?.keyFob?.id,
        mobile_user_id: data.mobile_user_id
      };

      /* Post data using the API service */
      const response = await parkingServices.addQueueRequestAPI(dataToSendOb); // Send the data as an object

      CustomNotification.showSuccessMessage(response?.message);
      resetField();
      props.handleRefreshList();
      props.handleOnClose();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableLoading();
    }
  };

  return {
    form,
    handleBack,
    resetField,
    handleChangeKeyFob,
    handleVerifyKeyFOB,
    handleSubmit,
    handleUserNameChange,
    isSubmitDisabled,
    loading,
    keyFOBDetails,
    selectedUserName // To auto-fill the user name
  };
};

export default useCreateParking;
